import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { get } from 'lodash';
import moment, { LocaleSpecifier } from 'moment';
import Video from 'twilio-video';

import { appointmentsResource, telehealthsResources } from '@lib/resources';
import { selectLocale } from '@lib/selectors';
import { TelehealthStepsEnum } from 'screens/TelehealthScreen/utils/enums';
import { TelehealthDetails } from 'screens/TelehealthScreen/utils/types';
import { useActiveSinkId } from 'screens/TelehealthScreen/hooks';
import { saveTelehealthLog } from 'screens/TelehealthScreen/utils/helpers';

export const LS_TH_LOG_DATA = 'lh:th_log_data';

interface TelehealthSessionContextProps {
  activeSinkId: string;
  appointmentId: string;
  currentStep: TelehealthStepsEnum;
  details: TelehealthDetails;
  isGuestCredentialsRoute: boolean;
  guestName: string;
  loading: boolean;
  participantsHidden: boolean;
  roomToken: string;
  setCurrentStep: React.Dispatch<any>;
  setParticipantsHidden: React.Dispatch<any>;
  token: string;
  updateActiveSinkId(sinkId: string): void;
  updateSessionDetails: React.Dispatch<any>;
  setTelehealthFeedbackId: React.Dispatch<React.SetStateAction<string>>;
  telehealthFeedbackId: string;
}

interface TelehealthSessionProviderProps {
  appointmentId: string;
  children: React.ReactNode;
  guestName: string;
  isGuestCredentialsRoute: boolean;
  roomToken: string;
  token: string;
}

export const TelehealthSessionContext = createContext<TelehealthSessionContextProps>(
  {} as TelehealthSessionContextProps,
);

export const TelehealthSessionProvider: React.FC<TelehealthSessionProviderProps> = ({
  appointmentId,
  children,
  guestName,
  isGuestCredentialsRoute,
  roomToken,
  token,
}) => {
  const [details, setDetails] = useState<TelehealthDetails>({
    isGuestParticipant: !!guestName,
    participant: guestName || '',
  });
  const [activeSinkId, updateActiveSinkId] = useActiveSinkId();
  const [participantsHidden, setParticipantsHidden] = useState(false);
  const [loading, setLoading] = useState(false);
  const currentLocale = useSelector(selectLocale);
  const dispatch = useDispatch();
  const initialStep = isGuestCredentialsRoute ? TelehealthStepsEnum.GuestCredentials : TelehealthStepsEnum.Readiness;
  const [currentStep, setCurrentStep] = useState(!Video.isSupported ? TelehealthStepsEnum.BrowserError : initialStep);
  const appointment = useSelector(appointmentsResource.makeSelectEntity((appointmentId || '') as string));
  const isSessionEnded = get(appointment, 'status', '') === 'ended' || details.status === 'ended';
  const [telehealthFeedbackId, setTelehealthFeedbackId] = useState('');

  const updateSessionDetails = useCallback((newDetails: TelehealthDetails) => {
    saveTelehealthLog('Patient/Guest - Updating session details', newDetails);
    setDetails((prevDetails: TelehealthDetails) => ({ ...prevDetails, ...newDetails }));
  }, []);

  const getGuestSessionDetails = useCallback(() => {
    setLoading(true);

    saveTelehealthLog('Getting guest details');

    dispatch(
      telehealthsResources.request({
        method: 'get',
        headers: {
          'x-access-token': token,
        },
        url: `/telehealths/session/${appointmentId}/guest/details`,
        meta: {
          callbackSuccess: ({ data }: any) => {
            updateSessionDetails({
              participant: guestName,
              provider: get(data, 'providerName', ''),
              facility: get(data, 'facilityName', ''),
              facilityPhone: get(data, 'facilityPhone', ''),
              date: moment(get(data, 'appointmentDate', '')).locale(currentLocale as LocaleSpecifier),
              isGuestParticipant: !!guestName,
              isStaffCall: get(data, 'providerName', '') === '',
            });

            setLoading(false);
          },
        },
      }),
    );
  }, [appointmentId, currentLocale, dispatch, guestName, token, updateSessionDetails]);

  const getSessionDetails = useCallback(() => {
    const connection =
      (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
    const connectionData = connection
      ? {
          type: connection.type,
          effectiveType: connection.effectiveType,
          downlink: connection.downlink,
          downlinkMax: connection.downlinkMax,
          rtt: connection.rtt,
        }
      : {};
    setLoading(true);

    saveTelehealthLog('Getting patient details', connectionData);

    dispatch(
      telehealthsResources.request({
        method: 'get',
        headers: {
          'x-access-token': token,
        },
        url: `/telehealths/session/${appointmentId}`,
        meta: {
          callbackSuccess: ({ data }: any) => {
            updateSessionDetails({
              status: get(data, 'status', ''),
              participants: get(data, 'participants', ''),
              endedAt: get(data, 'endedAt', ''),
              endedBy: get(data, 'endedBy', ''),
              staff: get(data, 'staff.name', ''),
              isStaffCall: !!get(data, 'staff.name', false),
              telehealthId: get(data, '_id', ''),
              zoomUrl: get(data, 'sessionDetails.zoomUrl', ''),
            });

            if (guestName) {
              setLoading(false);
            }
          },
          callbackError: () => {
            if (guestName) {
              setLoading(false);
            }
          },
        },
      }),
    );

    // storing participant network information
    if (connection) {
      dispatch(
        telehealthsResources.request({
          method: 'post',
          headers: {
            'x-access-token': token,
          },
          url: `/telehealths/session/${appointmentId}/connection`,
          data: {
            connection: connectionData,
          },
          meta: {
            ignoreErrors: true, // this error used to happen when the there is any issue related to connection
          },
        }),
      );
    }
  }, [appointmentId, dispatch, guestName, token, updateSessionDetails]);

  const fetchAppointment = useCallback(() => {
    saveTelehealthLog('Getting appointment details');

    dispatch(
      appointmentsResource.fetch({
        id: appointmentId,
        meta: {
          includeLookups: {
            patient: {},
            provider: {},
            facility: {},
          },
          resolveLookups: true,
        },
      }),
    );
  }, [appointmentId, dispatch]);

  useEffect(() => {
    // skip readiness view in case the appointment is already ended
    if (isSessionEnded) {
      saveTelehealthLog('Patient/Guest - Session already ended');
      setCurrentStep(TelehealthStepsEnum.EndedSession);
    }
  }, [isSessionEnded]);

  useEffect(() => {
    if (details.zoomUrl) {
      setCurrentStep(TelehealthStepsEnum.RedirectingZoom);
      window.location.assign(details.zoomUrl);
    }
  }, [details]);

  // initiating log data object with the parameters passed to the page;
  useEffect(() => {
    saveTelehealthLog('Updating session details', {
      appointmentId,
      guestName,
      isGuestCredentialsRoute,
      roomToken,
      token,
    });
  }, [appointmentId, guestName, isGuestCredentialsRoute, roomToken, token]);

  // updating log object with any new data;
  useEffect(() => {
    saveTelehealthLog('Updating session details', details);
  }, [details]);

  // get appointment/tehelealth data
  useEffect(() => {
    if (currentStep === TelehealthStepsEnum.Readiness) {
      if (isGuestCredentialsRoute) {
        getGuestSessionDetails();
      } else {
        getSessionDetails();

        if (!guestName) {
          fetchAppointment();
        }
      }
    }
  }, [currentStep, fetchAppointment, getSessionDetails, getGuestSessionDetails, guestName, isGuestCredentialsRoute]);

  // update appointment/telehealth data after appointment fetch
  useEffect(() => {
    if (appointment) {
      updateSessionDetails({
        participant: guestName || get(appointment, 'patient.name', ''),
        provider: get(appointment, 'provider.name', ''),
        facility: get(appointment, 'facility.name', ''),
        facilityPhone: get(appointment, 'facility.phone', ''),
        date: moment(get(appointment, 'date', '')).locale(currentLocale as LocaleSpecifier),
        isGuestParticipant: !!guestName,
        telehealthId: get(appointment, 'telehealth', ''),
      });

      setLoading(false);
    }
  }, [appointment, currentLocale, guestName, updateSessionDetails]);

  const contextValue = useMemo(
    () => ({
      activeSinkId,
      appointmentId,
      currentStep,
      details,
      isGuestCredentialsRoute,
      guestName,
      loading,
      participantsHidden,
      roomToken,
      setCurrentStep,
      setParticipantsHidden,
      token,
      updateActiveSinkId,
      updateSessionDetails,
      telehealthFeedbackId,
      setTelehealthFeedbackId,
    }),
    [
      activeSinkId,
      appointmentId,
      currentStep,
      details,
      isGuestCredentialsRoute,
      guestName,
      loading,
      participantsHidden,
      roomToken,
      setParticipantsHidden,
      token,
      updateActiveSinkId,
      updateSessionDetails,
      telehealthFeedbackId,
    ],
  );

  return <TelehealthSessionContext.Provider value={contextValue}>{children}</TelehealthSessionContext.Provider>;
};

export default TelehealthSessionProvider;
